import { z } from 'zod';
import { SportNameSchema, type SportName } from './profile.js';

/**
 * F33 — public-facing trainer package OFFERING definitions (D6/D8). F33 owns
 * the definitions; F35 owns the trainer↔platform billing. No purchase/balance
 * ledger at F33 (`validityDays` is display + a reserved seam).
 */

export const TRAINER_PACKAGE_NAME_MAX = 80;
export const TRAINER_PACKAGE_DESCRIPTION_MAX = 280;
export const TRAINER_PACKAGE_SESSION_DURATIONS = [30, 45, 60, 90, 120] as const;

export const TRAINER_PACKAGE_TYPE_VALUES = ['single', 'multi', 'monthly'] as const;
export type TrainerPackageType = (typeof TRAINER_PACKAGE_TYPE_VALUES)[number];
export const TrainerPackageTypeSchema = z.enum(TRAINER_PACKAGE_TYPE_VALUES);

export type TrainerPackage = {
  readonly id: string;
  readonly trainerUserId: string;
  readonly name: string;
  readonly description: string | null;
  readonly packageType: TrainerPackageType;
  readonly sportName: SportName | null;
  readonly sessionCount: number;
  readonly sessionDurationMinutes: number;
  readonly priceAmount: number;
  readonly priceCurrency: string;
  readonly validityDays: number | null;
  readonly isActive: boolean;
  readonly sortOrder: number;
  readonly countryCode: string;
  readonly createdAt: string;
  readonly updatedAt: string;
};

const baseFields = {
  name: z.string().min(1).max(TRAINER_PACKAGE_NAME_MAX),
  description: z.string().max(TRAINER_PACKAGE_DESCRIPTION_MAX).nullable().optional(),
  packageType: TrainerPackageTypeSchema,
  sportName: SportNameSchema.nullable().optional(),
  sessionCount: z.number().int().min(1).max(1000),
  sessionDurationMinutes: z.union([
    z.literal(30),
    z.literal(45),
    z.literal(60),
    z.literal(90),
    z.literal(120),
  ]),
  priceAmount: z.number().int().min(0),
  validityDays: z.number().int().min(1).max(3650).nullable().optional(),
  sortOrder: z.number().int().min(0).optional(),
} as const;

export const CreateTrainerPackageInputSchema = z
  .object(baseFields)
  .strict()
  .refine(
    (v) => v.packageType !== 'single' || v.sessionCount === 1,
    "A 'single' package must have sessionCount = 1",
  );
export type CreateTrainerPackageInput = z.infer<typeof CreateTrainerPackageInputSchema>;

export const UpdateTrainerPackageInputSchema = z
  .object({
    ...baseFields,
    isActive: z.boolean().optional(),
  })
  .partial()
  .strict();
export type UpdateTrainerPackageInput = z.infer<typeof UpdateTrainerPackageInputSchema>;
